import React, { useEffect, useRef, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { DEFAULT_MSA_TRIAL_DAYS, validMsaTrialDays } from '../../functions/src/common/msaTrial';
import { managementButtonStyles } from '../common/managementButtonStyles';
import { formatLocalDateTime } from '../utils/localDateTime';

interface TrialLink {
    url: string;
    trialDays: number;
    expiresAt: string;
}

const styles = {
    dialog: 'm-auto w-[calc(100%-2rem)] max-w-xl rounded-2xl border border-slate-200 bg-white p-6 text-slate-900 shadow-2xl backdrop:bg-slate-950/60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100',
    input: 'mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100',
    muted: 'text-sm text-slate-500 dark:text-slate-400',
    button: `rounded-lg px-4 py-2 text-sm font-bold ${managementButtonStyles.primary}`,
};

const FreeTrialLinkModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    const dialog = useRef<HTMLDialogElement>(null);
    const [days, setDays] = useState(String(DEFAULT_MSA_TRIAL_DAYS));
    const [link, setLink] = useState<TrialLink | null>(null);
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState('');
    useEffect(() => {
        const element = dialog.current;
        element?.showModal();
        return () => element?.close();
    }, []);
    const generate = async (event: React.FormEvent) => {
        event.preventDefault();
        const trialDays = Number(days);
        if (!validMsaTrialDays(trialDays)) {
            setError('Enter a positive whole number of trial days.');
            return;
        }
        setBusy(true);
        setError('');
        setCopied(false);
        try {
            const response = await httpsCallable<{ trialDays: number }, TrialLink>(getFunctions(undefined, 'us-central1'), 'createMsaTrialLink')({ trialDays });
            setLink(response.data);
        } catch (failure) {
            setError(failure instanceof Error ? failure.message : 'The free trial link could not be created.');
        } finally {
            setBusy(false);
        }
    };
    const copy = async () => {
        if (!link)
            return;
        setError('');
        try {
            await navigator.clipboard.writeText(link.url);
            setCopied(true);
        } catch {
            setError('Could not copy the link. Select the link and copy it manually.');
        }
    };
    return <dialog ref={dialog} aria-labelledby="free-trial-link-title" onClose={onClose} onCancel={event => { if (busy) event.preventDefault(); }} className={styles.dialog}>
        <div className="mb-5 flex items-center justify-between gap-4">
            <h2 id="free-trial-link-title" className="text-xl font-extrabold">Create a free trial link</h2>
            <button type="button" disabled={busy} onClick={onClose} aria-label="Close free trial link window" className="rounded-lg px-3 py-1 text-xl disabled:opacity-50">×</button>
        </div>
        <form onSubmit={generate} className="space-y-5">
            <p className={styles.muted}>Each link can create one free trial. Unused links expire 30 days after generation.</p>
            <label className="block text-sm font-bold">Free trial term (days)<input autoFocus type="number" min="1" step="1" required disabled={busy} value={days} onChange={event => setDays(event.target.value)} className={styles.input} /></label>
            <button disabled={busy} className={styles.button}>{busy ? 'Generating…' : 'Generate'}</button>
        </form>
        {link && <section aria-live="polite" className="mt-6 space-y-3 border-t border-slate-200 pt-5 dark:border-slate-700">
            <label htmlFor="free-trial-link" className="text-sm font-bold">Free trial link</label>
            <div className="flex items-center gap-2"><input id="free-trial-link" readOnly value={link.url} onFocus={event => event.target.select()} className={`${styles.input} mt-0 min-w-0`} /><button type="button" onClick={() => void copy()} className={styles.button}>{copied ? 'Copied' : 'Copy'}</button></div>
            <p className={styles.muted}>{link.trialDays}-day trial, starting when signup completes. Link expires {formatLocalDateTime(link.expiresAt)}.</p>
            {copied && <p role="status" className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">Link copied to clipboard.</p>}
        </section>}
        {error && <p role="alert" className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-200">{error}</p>}
    </dialog>;
};

export default FreeTrialLinkModal;
